import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum BookingPaymentStatus {
  charged = 'charged',
  markedAsPaid = 'marked_as_paid',
  failed = 'failed',
  failing = 'failing',
  hasPaymentInformation = 'has_payment_information',
  invalidPaymentInformation = 'invalid_payment_information',
}

export default class BookingPaymentModel extends Model {
  @attr('boolean') cardValid!: boolean;
  @attr('string') paymentStatus!: BookingPaymentStatus;
  @attr('string') stripeError!: string;
  @attr('boolean') needsCharging!: boolean;
  @attr('string') externalCustomerLink!: string;

  @belongsTo('booking', { async: false, inverse: 'bookingPayment' })
  booking!: ModelRegistry['booking'];

  @hasMany('payment', { async: false, inverse: 'bookingPayment' })
  payments!: ModelRegistry['payment'][];

  @cached
  get hasPaymentInformation(): boolean {
    return this.paymentStatus === BookingPaymentStatus.hasPaymentInformation;
  }

  @cached
  get hasPayments(): boolean {
    return this.payments.length > 0;
  }

  @cached
  get canBeMarkedAsPaid(): boolean {
    const isEveryPaid = this.payments.every((payment) => payment.isPaid);

    return !(this.hasPayments && isEveryPaid);
  }

  @cached
  get hasOutstandingSplitPayments(): boolean {
    return this.hasPayments && this.payments.some((payment) => !payment.isPaid);
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'booking-payment': BookingPaymentModel;
  }
}
